import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import './ShopTabs.scss';

import { hasProductIdsPromoted, isShopper } from '../../../Helpers/user_helpers';
import { setUrlParam, removeUrlParam } from '../../../Helpers/helpers';
import Select from '../../General/Select';

const ShopTabs = props => {
  const { user, isYourShop, activeRefinementObjects } = props;
  const { activeCurator, activeCuratorGroup, activeBrands, activeCategory, activeDepartment } = activeRefinementObjects || {};

  const tabs = [
    {
      label: 'Latest Finds',
      mobileLabel: 'Latest',
      onClick: () => props.goToTab('latest'),
      isActive: props.tab === 'latest',
      isHidden: !!props.query
    },
    {
      value: `collections`,
      onClick: () => props.goToTab('collections'),
      label: isYourShop && isShopper(user) ? 'Wishlists' : 'Collections',
      mobileLabel: isYourShop && isShopper(user) ? 'Wishlists' : 'Collections',
      isActive: props.tab === 'collections',
      sublabel: 'Curated Lists.',
      isHidden: !props.isCuratedShop || !!props.query
    },
    {
      label: 'Most Popular',
      mobileLabel: 'Popular',
      onClick: () => props.goToTab('popular'),
      isActive: props.tab === 'popular',
      isHidden: (isShopper(user) && isYourShop) || !!props.query
    },
    {
      value: 'consults',
      label: 'Consults',
      onClick: () => props.goToTab('consults'),
      isActive: props.tab === 'consults',
      mobileLabel: 'Consults',
      sublabel: '1 on 1 consultations.',
      isHidden: !activeCurator || !activeCurator.showConsultsTabInShop || !!props.query
    },
    {
      label: 'For You',
      mobileLabel: 'For You',
      onClick: () => props.goToTab('foryou'),
      isActive: props.tab === 'foryou',
      isHidden: isYourShop || !!props.query,
      isDisabled: !hasProductIdsPromoted(user),
      isDisabledWarning: !hasProductIdsPromoted(user) ? 'Heart a few products to get personalized recommendations' : null
    }
  ].filter(c => !c.isHidden);

  // Search Behavior
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isInSearchMode, setIsInSearchMode] = React.useState(false);
  const toggleSearchMode = () => {
    // If we have a query we want to set the input value to the query
    if (props.query) setSearchQuery(props.query);
    setIsInSearchMode(!isInSearchMode);
  };
  const clearSearch = () => {
    setSearchQuery('');
    setIsInSearchMode(false);
    removeUrlParam('query');
  };
  const clearGlobalSearch = () => {
    clearSearch();
    props.setQuery('');
    removeUrlParam('query');
  };
  const blurSearch = () => !searchQuery && setIsInSearchMode(false);
  const closeSearchMode = () => setIsInSearchMode(false);

  const submitSearch = e => {
    e?.preventDefault();
    props.setQuery(searchQuery);
    setUrlParam('query', searchQuery);
    clearSearch();

    // CLear other results
    props.clearCategoryId();
    props.clearDepartmentId();
    props.clearIndustryId();
    props.clearBrandIds();

    // Scroll to top of the results
    setTimeout(() => {
      const rect = props.shopTabsRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: rect.top + scrollTop - 44, // Offset for the header
        behavior: 'smooth'
      });
    }, 200);
  };

  // UI Handling
  const additionalClasses = {
    'in-search-mode': isInSearchMode
  };

  return (
    <div ref={props.shopTabsRef} className='shop-tabs-outer-container'>
      <div className={cn('shop-tabs', additionalClasses)}>
        <div className='search-container'>
          {isInSearchMode ? (
            <div className='is-searching-container-outer'>
              <form onSubmit={submitSearch} className='is-searching-container'>
                <div className='search-input-container has-actions'>
                  <div className='search-icon'>
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <input
                    autoFocus
                    onBlur={blurSearch} // REMOVE BEFORE LANDING
                    placeholder={
                      activeCurator
                        ? `Search ${activeCurator.name}${_.last(activeCurator.name) === 's' ? "'" : "'s"} Shop`
                        : activeCuratorGroup
                        ? `Search ${activeCuratorGroup.title}${_.last(activeCuratorGroup.title) === 's' ? "'" : "'s"} Finds`
                        : activeBrands?.length
                        ? `Search ${activeBrands[0].name}`
                        : activeCategory
                        ? `Search ${activeCategory.name}`
                        : activeDepartment
                        ? `Search ${activeDepartment.name}`
                        : 'Search Shop'
                    }
                    type='text'
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                  <div className={cn('submit-icon', { active: !!searchQuery })} onClick={submitSearch}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
                <div className={cn('clear-icon')} onClick={closeSearchMode}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              </form>
            </div>
          ) : (
            !props.query && (
              <div onClick={toggleSearchMode} className={cn('search-button', { 'search-again': !!props.query })}>
                <FontAwesomeIcon icon={faSearch} />
              </div>
            )
          )}
        </div>
        {props.query && !isInSearchMode && (
          <>
            <div className='shop-tab active clearable'>
              <div onClick={toggleSearchMode}>{props.query}</div>
              <div onClick={toggleSearchMode} className='clear-search-icon'>
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <div className='clear-search-label' onClick={clearGlobalSearch}>
                Clear Search
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </div>
          </>
        )}
        {!isInSearchMode &&
          tabs.map((tab, idx) => {
            const click = () => (tab.isActive ? null : tab.isDisabled ? window.ALERT.warn(tab.isDisabledWarning) : tab.onClick());
            return (
              <div className={cn('shop-tab', { active: tab.isActive })} key={tab.label + idx} onClick={click}>
                {tab.label}
              </div>
            );
          })}
      </div>
      {window.__ADMIN_CONTROL_MODE__ && (
        <div className='admin-control-mode-container'>
          <div className='admin-control-mode-select-container'>
            <Select
              disableSearch
              isDark
              options={[
                { value: 'default', label: 'Default Images', sublabel: 'Standard Combination' },
                { value: 'preview', label: 'Preview Only', sublabel: 'Products on White Backgrounds for Search Results' },
                { value: 'cover', label: 'Primary Only', sublabel: 'Full Cover Images for Product Landing Pages' }
              ]}
              value={props.imageStyleEnum}
              placeholder='Image Style'
              onChangeValue={newValue => props.setImageStyleEnum(newValue)}
              isSearching={false}
              isDisabled={false}
              isLoading={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

ShopTabs.propTypes = {
  user: PropTypes.object.isRequired,
  isYourShop: PropTypes.bool.isRequired,
  activeRefinementObjects: PropTypes.object.isRequired,
  goToTab: PropTypes.func.isRequired,
  tab: PropTypes.string.isRequired,
  imageStyleEnum: PropTypes.string.isRequired,
  setImageStyleEnum: PropTypes.func.isRequired,
  shopTabsRef: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const { user } = state;
  return { user };
};

export default connect(mapStateToProps, {
  // functions go here
})(ShopTabs);
